import { Component, Host, h, Prop, State, Fragment } from '@stencil/core';
import { addComment } from '../fetch-comments';

import store from '../store';
import { inputValidator } from '../../../global/utility/utils';

interface FormData {
  name: string;
  email: string;
  message: string;
  parentCommentId?: string;
  subscribe?: 'true' | 'false';
}

@Component({
  tag: 'ui-cms-add-comment',
  styleUrl: 'ui-cms-add-comment.scss',
  shadow: false,
})
export class UiCmsAddComment {
  /**
   * The id of the parent comment if this is a reply
   */
  @Prop() parentCommentId: string;

  /**
   * Object containing user submitted form data
   */
  @State() formData: FormData = {
    name: '',
    email: '',
    message: '',
    subscribe: 'false',
  };

  @State() error = false;

  @State() commentSuccess = false;

  private form: HTMLElement;

  private nameInput: HTMLInputElement;

  private emailInput: HTMLInputElement;

  private messageInput: HTMLTextAreaElement;

  private subscribe: HTMLInputElement;

  private focusFirstFieldWithError() {
    const formElements = Array.from(this.form.querySelectorAll('[form]'));
    const fieldWithError = formElements.find((field) =>
      field?.shadowRoot?.querySelector('[aria-invalid="true"]')
    ) as HTMLElement;

    fieldWithError.focus();
  }

  private sendCommentHandler = async (): Promise<void> => {
    if (this.parentCommentId) {
      this.formData.parentCommentId = this.parentCommentId;
    }

    if (this.subscribe?.checked) {
      this.formData.subscribe = 'true';
    }

    const editorFormData = {
      message: this.formData.message,
      ...(this.parentCommentId && { parentCommentId: this.parentCommentId }),
    };

    const messageData = store.state.isEditMode ? editorFormData : { ...this.formData };

    if (this.validateForm()) {
      // Send comment
      const addedComment = await addComment({
        apiUrl: store.state.setCommentApiUrl,
        // Api Key needed only for public comments and not for Edit Mode
        // On Edit mode sending it will produce some cors/403 errors on the api
        ...(!store.state.isEditMode && { apiKey: store.state.apiKey }),
        commentParams: {
          ...messageData,
        },
      });

      if (addedComment.status === 'success') {
        // reset form
        this.formData = {
          name: '',
          email: '',
          message: '',
        };
        // show success message
        this.commentSuccess = true;
      }
    } else {
      this.focusFirstFieldWithError();
    }
  };

  private addInputChangeListeners(): void {
    this.nameInput?.addEventListener('sd-input', this.onChangeHandler);
    this.emailInput?.addEventListener('sd-input', this.onChangeHandler);
    this.messageInput.addEventListener('sd-input', this.onChangeHandler);
  }

  private onChangeHandler = (event: Event): void => {
    const { name, value } = event.target as HTMLUiInputElement;
    this.updateFormData(name, value);
  };

  private updateFormData = (name: string, value: string): void => {
    this.formData = {
      ...this.formData,
      [name]: value,
    };
    // Reset error state
    if (this.error) {
      this.validateForm();
    }
  };

  private setInputFeedback(
    isFieldValid: boolean,
    fieldReference: HTMLInputElement | HTMLTextAreaElement,
    selector: string
  ) {
    if (isFieldValid) {
      fieldReference.setCustomValidity(store.state.i18n.emptyField);
      fieldReference.reportValidity();
      fieldReference?.shadowRoot?.querySelector(selector)?.setAttribute('aria-invalid', 'true');
    } else {
      fieldReference?.shadowRoot?.querySelector(selector)?.setAttribute('aria-invalid', 'false');
    }
  }

  private validateForm = (): boolean => {
    const { name, email, message } = this.formData;

    // reset input feedback
    this.nameInput?.setCustomValidity('');
    this.emailInput?.setCustomValidity('');
    this.messageInput.setCustomValidity('');
    this.nameInput?.reportValidity();
    this.emailInput?.reportValidity();
    this.messageInput.reportValidity();

    const isValid = store.state.isEditMode
      ? inputValidator('textarea', message)
      : inputValidator('text', name) && inputValidator('email', email) && inputValidator('textarea', message);
    this.error = !isValid;

    const isNameFieldInvalid = this.nameInput && !inputValidator('text', name);
    const isEmailFieldInvalid = this.nameInput && !inputValidator('email', email);
    const isMessageFieldInvalid = this.nameInput && !inputValidator('textarea', message);

    // Show sd input feedback
    this.setInputFeedback(isNameFieldInvalid, this.nameInput, 'input');
    this.setInputFeedback(isEmailFieldInvalid, this.emailInput, 'input');
    this.setInputFeedback(isMessageFieldInvalid, this.messageInput, 'textarea');

    return isValid;
  };

  componentDidLoad() {
    this.addInputChangeListeners();
  }

  render() {
    return (
      <Host>
        <div class="comment-form">
          <h2 class="sd-headline">
            {store.state.isMainCommentFormOpen ? store.state.i18n.leaveComment : store.state.i18n.replyToComment}
          </h2>
          {!this.commentSuccess ? (
            <Fragment>
              <div
                class="comment-form__fields"
                ref={(el: HTMLInputElement) => {
                  this.form = el;
                }}
              >
                {!store.state.isEditMode && (
                  <Fragment>
                    <sd-input
                      label={store.state.i18n.name}
                      name="name"
                      ref={(el: HTMLInputElement) => {
                        this.nameInput = el;
                      }}
                      required
                      type="text"
                      placeholder={store.state.i18n.name}
                      value={this.formData.name}
                    ></sd-input>
                    <sd-input
                      label={store.state.i18n.email}
                      name="email"
                      type="email"
                      ref={(el: HTMLInputElement) => {
                        this.emailInput = el;
                      }}
                      placeholder={store.state.i18n.email}
                      value={this.formData.email}
                      required
                    ></sd-input>
                  </Fragment>
                )}
                <sd-textarea
                  label={store.state.i18n.message}
                  name="message"
                  placeholder={store.state.i18n.message}
                  ref={(el: HTMLTextAreaElement) => {
                    this.messageInput = el;
                  }}
                  rows={3}
                  value={this.formData.message}
                  required
                ></sd-textarea>
                {this.error && <p class="sd-paragraph error-message">{store.state.i18n.correctFieldsError}</p>}
              </div>
              {!store.state.isEditMode && (
                <sd-checkbox
                  ref={(el: HTMLInputElement) => {
                    this.subscribe = el;
                  }}
                >
                  {store.state.stayInformedText}
                </sd-checkbox>
              )}
              <sd-link href={store.state.netiquetteLink}>{store.state.i18n.netiquette}</sd-link>
              <sd-button onClick={() => this.sendCommentHandler()}>
                <sd-icon
                  library={window.ui.cmsState.iconLibrary}
                  name="union-investment/system/e-mail-send"
                  slot="icon-left"
                ></sd-icon>
                {store.state.i18n.sendComment}
              </sd-button>
              {!store.state.isEditMode && <p class="sd-paragraph" innerHTML={store.state.emailInfoText}></p>}
            </Fragment>
          ) : (
            <p class="sd-paragraph" innerHTML={store.state.i18n.successMessage}></p>
          )}
        </div>
      </Host>
    );
  }
}
